import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import type { Role } from '../hooks/useValidateSession';
import { buildInvitePositionMap, type InvitePositionOption } from './invitePositionOptions';

/** File types the invite upload accepts, for an input's `accept` attribute. */
export const INVITE_TEMPLATE_ACCEPT = '.xlsx,.xls,.csv';

export const isSupportedInviteFile = (file: File): boolean => {
    const name = file.name.toLowerCase();
    return ['.xlsx', '.xls', '.csv'].some((ext) => name.endsWith(ext));
};

/** Sheet holding org-wide invites; every other sheet is named for a location. */
const ALL_LOCATIONS_SHEET = 'All Locations';

/** Excel caps sheet names at 31 characters, so location tabs are truncated. */
const sheetNameFor = (title: string) => (title.length > 31 ? title.slice(0, 31) : title);

export interface InviteTemplateLocation {
    _id: string;
    title: string;
}

export interface ParsedInviteRow {
    email: string;
    /** Empty when the sheet's position did not match a known option. */
    position: Role | '';
    locationId: Array<string> | 'all';
}

export interface ParsedInviteWorkbook {
    rows: ParsedInviteRow[];
    /** Sheets whose name matched no location, so their rows were dropped. */
    skippedSheets: number;
}

/**
 * Downloads the bulk-invite workbook: one sheet per location plus an
 * "All Locations" sheet, each with Email/Position columns and a position
 * dropdown, so the location a user belongs to is the sheet they are listed on.
 */
export const downloadInviteTemplate = async (
    locations: InviteTemplateLocation[],
    positionOptions: InvitePositionOption[],
): Promise<void> => {
    const workbook = new ExcelJS.Workbook();
    const positionLabels = positionOptions.map((option) => option.label);
    const formulaList = `"${positionLabels.join(',')}"`;

    const addSheet = (name: string) => {
        const sheet = workbook.addWorksheet(name);
        const headerRow = sheet.getRow(1);
        headerRow.values = ['Email', 'Position'];
        headerRow.font = { bold: true };
        sheet.getColumn(1).width = 30;
        sheet.getColumn(2).width = 20;
        for (let row = 2; row <= 200; row++) {
            sheet.getCell(`B${row}`).dataValidation = {
                type: 'list',
                allowBlank: true,
                formulae: [formulaList],
                showErrorMessage: true,
                errorTitle: 'Invalid Position',
                error: `Please select one of: ${positionLabels.join(', ')}`,
            };
        }
    };

    addSheet(ALL_LOCATIONS_SHEET);
    const usedNames = new Set<string>([ALL_LOCATIONS_SHEET.toLowerCase()]);
    locations.forEach((location) => {
        const name = sheetNameFor(location.title);
        const key = name.toLowerCase();
        if (usedNames.has(key)) return;
        usedNames.add(key);
        addSheet(name);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
        new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        'invite-users-template.xlsx',
    );
};

const cellText = (value: ExcelJS.CellValue): string => {
    if (value == null) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    if (typeof value === 'object') {
        if ('richText' in value) return value.richText.map((part: { text: string }) => part.text).join('');
        if ('text' in value) return String((value as { text: string }).text);
        if ('result' in value) return cellText((value as { result: ExcelJS.CellValue }).result);
    }
    return '';
};

/**
 * Reads a filled-in invite workbook back into rows. Each sheet's name decides
 * the location, and positions are matched by label, slug, or a known alias, so
 * a spreadsheet edited by hand still lands on the right values. Rows with no
 * email are ignored; rows on a sheet that matches no location are dropped and
 * counted in `skippedSheets`.
 */
export const parseInviteWorkbook = async (
    file: File,
    options: {
        locations: InviteTemplateLocation[];
        positionOptions: InvitePositionOption[];
        /** Invoicing-only orgs have one position, so any parsed value is ignored. */
        isInvoicingOrg?: boolean;
    },
): Promise<ParsedInviteWorkbook> => {
    const { locations, positionOptions, isInvoicingOrg = false } = options;
    const buffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);

    const locationNameToId = new Map(
        locations.map((location) => [location.title.trim().toLowerCase(), location._id]),
    );
    const sheetNameToLocationId = new Map(
        locations.map((location) => [
            sheetNameFor(location.title).trim().toLowerCase(),
            location._id,
        ]),
    );
    const positionLabelToRole = new Map(
        positionOptions.map((option) => [option.label.toLowerCase(), option.value]),
    );
    const positionMap = buildInvitePositionMap(positionOptions);

    const rows: ParsedInviteRow[] = [];
    let skippedSheets = 0;

    workbook.eachSheet((sheet) => {
        const sheetKey = sheet.name.trim().toLowerCase();
        const isAllSheet = sheetKey === ALL_LOCATIONS_SHEET.toLowerCase();
        const locationId = isAllSheet
            ? null
            : sheetNameToLocationId.get(sheetKey) ?? locationNameToId.get(sheetKey);

        if (!isAllSheet && !locationId) {
            skippedSheets++;
            return;
        }

        sheet.eachRow((row, rowNumber) => {
            if (rowNumber === 1) return;
            const email = cellText(row.getCell(1).value).trim();
            if (!email) return;

            const rawPosition = cellText(row.getCell(2).value).trim().toLowerCase();
            const position = isInvoicingOrg
                ? ('invoicing' as Role)
                : positionLabelToRole.get(rawPosition) ?? positionMap[rawPosition] ?? '';

            rows.push({
                email,
                position,
                locationId: isAllSheet ? 'all' : [locationId as string],
            });
        });
    });

    return { rows, skippedSheets };
};
